
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import { v4 as generateUuid } from "uuid"

import WorkReportQuery from "@/graphql/workreport/workreport.graphql"
import WorkReportsForJobQuery from "@/graphql/job/workReportsForJob.graphql"
import JobDetailQuery from "../../graphql/job/jobdetail.graphql"
import CurrentWorkReportForJobQuery from "@/graphql/job/currentWorkReportForJob.graphql"
import GroupsQuery from "@/graphql/group/groups.graphql"
import * as cdn from "@coredination/components"
import UpdateWorkReport from "@/graphql/workreport/updateworkreport.graphql"
import CreateWorkReport from "@/graphql/workreport/createworkreport.graphql"
import SetUserState from "../../graphql/job/setuserstate.graphql"

import { CircularLoader } from "@coredination/components"
import { Formatting } from "@/util/formatting"
import { navbar, NavbarOption } from "@/components/NavbarOptions.vue"
import modaldialog from "@/components/dialogs/modaldialog"

const dateFormat = new Formatting.DateFormatting()

@Component({
    apollo: {
        workReport: {
            query: WorkReportQuery,
            skip() {
                return !this.uuid
            },
            variables() {
                return { uuid: this.uuid }
            },
            update(data) {
                this.undoModel = JSON.parse(JSON.stringify(data.workReport))
                this.changed = false
                return data.workReport
            },
        },
        job: {
            query: JobDetailQuery,
            skip() {
                return !this.jobUuid
            },
            variables() {
                return { uuid: this.jobUuid }
            },
        },
        workReportsForJob: {
            query: WorkReportsForJobQuery,
            skip() {
                return !this.jobUuid
            },
            variables() {
                return { jobUuid: this.jobUuid }
            },
        },
        currentWorkReportForJob: {
            query: CurrentWorkReportForJobQuery,
            skip() {
                return !this.jobUuid
            },
            variables() {
                return { jobUuid: this.jobUuid, userId: this.userId }
            },
        },
        me: cdn.graphql.me,
        groups: GroupsQuery,
    },
    components: {
        CircularLoader,
        // form fields
        DateField: () => import("@/components/fields/DateField.vue"),
        SelectStateField: () => import("@/components/fields/SelectStateField.vue"),
        GroupField: () => import("@/components/fields/GroupField.vue"),
        CustomerField: () => import("@/components/fields/CustomerField.vue"),
        TemplateSelectField: () => import("@/components/fields/TemplateSelectField.vue"),
        ActivityField: () => import("@/components/fields/ActivityField.vue"),
        VehicleField: () => import("@/components/fields/VehicleField.vue"),
        FreeTextField: () => import("@/components/fields/FreeTextField.vue"),
        TimeField: () => import("@/components/fields/TimeField.vue"),
        InvoicedField: () => import("@/components/fields/InvoicedField.vue"),
        ItemsTable: () => import("@/components/misc/ItemsTable.vue"),
        DurationField: () => import("@/components/fields/DurationField.vue"),
        LabelField: () => import("@/components/fields/LabelField.vue"),
        CustomFieldsField: () => import("@/components/fields/CustomFieldsField.vue"),
        UnsavedChangesDialog: () => import("@/components/dialogs/UnsavedChangesDialog.vue"),
        ExcludedFieldsDialog: () => import("@/components/dialogs/ExcludedFieldsDialog.vue"),
        RequiredFieldsDialog: () => import("@/components/dialogs/RequiredFieldsDialog.vue"),
    },
    props: ["new", "uuid", "jobUuid", "userId"],
    beforeRouteLeave(to, from, next) {
        const dis = this as WorkReportForm

        if (modaldialog.preventNavigation()) {
            console.log("modal dialog says no to navigation")
            return next(false)
        }

        // Prevent leaving on unsaved changes
        if (
            dis.changed ||
            ((dis.undoModel.uuid || dis.undoModel.groupUuid) &&
                JSON.stringify(dis.undoModel) != JSON.stringify(dis.model))
        ) {
            dis.showUnsavedChanges = true
            dis.discardChangesNext = next
            return next(false)
        }

        next()
    },
})
export default class WorkReportForm extends Vue {
    @Prop()
    uuid?: string
    @Prop()
    templateUuid?: any
    @Prop()
    groupUuid?: any
    @Prop()
    jobUuid?: any
    @Prop()
    userId?: any

    undoModel: any = {}
    me: any = {}
    groups?: any[]

    workReport: any = { startTime: null, endTime: null }
    workReportsForJob: any[] = []
    job: any = {}
    myState: string = ""
    currentWorkReportForJob: any = {}
    errorRequiredFields: any[] = []

    itemsFieldUpdateKey: number = 0
    changed = false
    showUnsavedChanges = false
    discardChangesNext: any = null
    selectingExcludedFields = false
    selectingRequiredFields = false

    get model() {
        if (this.workReport) {
            if (this.workReport.workDate) {
                if (this.workReport.startTime) {
                    var startDate: Date = new Date(this.workReport.startTime)
                    var workDate: Date = new Date(this.workReport.workDate)
                    workDate.setHours(startDate.getHours())
                    workDate.setMinutes(startDate.getMinutes())
                    this.workReport.startTime = workDate.getTime()
                }
                if (this.workReport.endTime) {
                    var endDate: Date = new Date(this.workReport.endTime)
                    var workDate: Date = new Date(this.workReport.workDate)
                    workDate.setHours(endDate.getHours())
                    workDate.setMinutes(endDate.getMinutes())
                    this.workReport.endTime = workDate.getTime()
                }
            }
        }
        return this.workReport
    }
    set model(updatedWorkReport: any) {
        this.workReport = updatedWorkReport
    }
    get required(): any {
        return this.model && this.model.requiredFields
            ? this.model.requiredFields.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
            : null
    }
    get excluded(): any {
        return this.model && this.model.excludedFields
            ? this.model.excludedFields.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
            : null
    }

    @Watch("model")
    watchModel(newModel: any) {
        if (!this.undoModel.uuid || this.undoModel.uuid != newModel.uuid) {
            this.undoModel = JSON.parse(JSON.stringify(newModel))
        }
    }

    @Watch("model", { deep: true })
    deepWatchModel() {
        if (
            (this.undoModel.uuid || this.undoModel.groupUuid) &&
            JSON.stringify(this.undoModel) != JSON.stringify(this.model)
        )
            this.changed = true
    }

    myWorkDate: any
    @Watch("model.workDate")
    dateChange(newDate: any) {
        if (newDate) this.myWorkDate = new Date(newDate).setHours(0,0,0,0)
        this.model.workDate = this.myWorkDate
    }

    new?: boolean

    mounted() {
        navbar?.setOptions(this.options)
        // Guard against leaving/reloading with unsaved changes
        window.onbeforeunload = () => {
            if (this.changed) return this.$t("unsavedChangesTitle")
        }
        if (this.jobUuid) {
            // /workreport/job/:jobUuid route
            // Save workreport based on currentWorkReportForJob
            // and redirect after save
            this.$apollo
                .query({
                    query: CurrentWorkReportForJobQuery,
                    variables: { jobUuid: this.jobUuid },
                })
                .then((response) => {
                    this.model = Object.assign({}, response.data.currentWorkReportForJob)
                    this.undoModel = JSON.parse(JSON.stringify(this.model))
                })
                .catch((error) => {
                    console.error(error)
                })
        } else if (this.new) {
            // /workreport/new route
            // Save empty workreport and redirect
            var groupUuid: string = this.me.primaryGroupUuid
            var groupName: string = this.me.primaryGroupName
            if (groupUuid) {
                this.workReport.groupUuid = groupUuid
                this.workReport.groupName = groupName
            }
        } else {
            // /workreport/:uuid
            this.$apollo.queries.workReport.refetch()
        }
        this.$root.$emit("disableTabNavigation")
    }

    destroyed() {
        window.onbeforeunload = null
    }

    getInputObj(model: any): any {
        return {
            workDate: model.workDate ? model.workDate : null,
            groupUuid: this.model.groupUuid ? this.model.groupUuid : null,
            fromTemplateId: model.fromTemplateId ? model.fromTemplateId : null,
            customerUuid: model.customerUuid ? model.customerUuid : null,
            customerProjectUuid: model.customerProjectUuid ? model.customerProjectUuid : null,
            activityId: model.activityId ? model.activityId : null,
            vehicleId: model.vehicleId ? model.vehicleId : null,
            description: model.description && model.description.trim().length > 0 ? model.description : null,
            startTime: model.startTime ? model.startTime : null,
            endTime: model.endTime ? model.endTime : null,
            toBeInvoiced: model.toBeInvoiced ? model.toBeInvoiced : false,
            pauseDuration: model.pauseDuration ? model.pauseDuration : null,
            workDuration: (!model.items || model.items.length == 0) && model.workDuration ? model.workDuration : null,
            items: model.items ? this.stripItems(model.items) : null,
            jobUuid: model.jobUuid ? model.jobUuid : null,
            excludedFields: model.excludedFields && model.excludedFields.length > 0 ? model.excludedFields : null,
            requiredFields: model.requiredFields && model.requiredFields.length > 0 ? model.requiredFields : null,
            labels: model.labels || null,
            customFields: this.stripTypeNameFromCustomFields(this.model.customFields) || null,
        }
    }

    changeExcludedFields(changed: string[]) {
        Vue.set(this.model, "excludedFields", changed)
    }

    changeRequiredFields(changed: string[]) {
        Vue.set(this.model, "requiredFields", changed)
    }

    userReportForJob(user: string) {
        if (this.workReportsForJob && this.workReportsForJob.length > 0) {
            return this.workReportsForJob.find(({ userId }) => userId === user)
        }
        return null
    }

    save() {
        var self: any = this
        if (!this.model.uuid) this.model.uuid = generateUuid()
        var uuid = this.model.uuid
        var userReport = this.userReportForJob(this.userId)
        if (userReport && userReport.uuid) uuid = userReport.uuid
        var input: any = this.getInputObj(this.model)
        if (!this.verifyRequired()) {
            ;(this as any).$toast.error(this.$t("required_field_error"), { icon: 'warning' })
            return
        }
        if (this.userId) input.userId = parseInt(this.userId)
        const jobUuid: string = this.jobUuid
        this.saving = true
        if ((this.job.multipleReports && this.userId) || (!this.job.multipleReports && this.userId && !userReport)) {
            return this.$apollo
                .mutate({
                    mutation: CreateWorkReport,
                    variables: { input },
                    update(proxy, response) {
                        var job = response.data.createWorkReport.jobUuid != null
                        self.invalidateCache(proxy, response, job)
                    },
                })
                .then(async (response) => {
                    //this.saveToast("workReport")
                    Object.assign(this.workReport, response.data.createWorkReport)
                    if (jobUuid) this.saveMyState()
                    else {
                        this.saving = this.changed = false
                        this.undoModel = {}
                        this.$router.back()
                    }
                })
                .catch((error) => {
                    this.saving = false
                    console.error(error)
                })
        }
        return this.$apollo
            .mutate({
                mutation: UpdateWorkReport,
                variables: { uuid, input },
                update(proxy, response) {
                    var job = response.data.updateWorkReport.jobUuid != null
                    self.invalidateCache(proxy, response, job)
                },
            })
            .then(async (response) => {
                Object.assign(this.workReport, response.data.updateWorkReport)
                if (jobUuid) this.saveMyState()
                else {
                    this.saving = this.changed = false
                    this.undoModel = {}
                    this.$router.back()
                }
            })
            .catch((error) => {
                this.saving = false
                console.error(error)
            })
    }

    saveMyState() {
        const uuid: string = this.jobUuid
        var variables = {
            uuid: uuid,
            state: this.myState,
            userId: this.me.id,
        }
        return this.$apollo
            .mutate({
                mutation: SetUserState,
                variables: variables,
                update(proxy, response) {
                    // Invalidate cache - TODO there is probably a better way?
                    // taken from WorkReportEdit.vue, modified for jobs
                    const hackProxy = proxy as any
                    Object.keys(hackProxy.data.data).forEach((key) => {
                        if (key.match(/(myJobs|myWorkReports|myWorkStats|myCurrentJob)/)) hackProxy.data.delete(key)
                    })
                },
            })
            .then(async (response: any) => {
                Object.assign(this.workReport, response.data.createWorkReport)
                //@ts-ignore
                var myResource = this.job.resources.find(elem => {
                    return elem?.userId == this.me?.id
                })
                myResource.state = this.myState
                this.saving = this.changed = false
                this.undoModel = {}
                this.$router.back()
            })
            .catch((error) => {
                this.saving = false
                console.error(error)
            })
    }

    invalidateCache(proxy: any, response: any, job?: boolean) {
        const hackProxy = proxy as any
        Object.keys(hackProxy.data.data).forEach((key) => {
            if (key.match(/(workreport|myWorkReports|myWorkStats|workReportsForJob)/)) hackProxy.data.delete(key)
            if (job && key.match(/(myJobs|myWorkStats|currentWorkReportForJob)/)) hackProxy.data.delete(key)
        })
    }

    // i hate this stupid hacky solution but i can't figure out a better solution
    // to the __typename-situation. screw it, i'll leave this until we've figured it out
    stripTypeNameFromCustomFields(customFields: any[]) {
        if (!customFields || customFields.length == 0) return null
        return customFields.map((custom: any) => {
            const { ["__typename"]: _, ...cleanCustomFields } = custom
            return cleanCustomFields
        })
    }

    get calculateWorkDuration() {
        // needs to get calculated based on start-, end-time and pause duration
        // if not set by itself
        // get inspired by the current frontendz
        return !this.model.items ? (this.model.workDuration ? this.model.workDuration : null) : null
    }

    stripItems(items: any) {
        var copy = JSON.parse(JSON.stringify(items))
        copy.forEach((item: any) => {
            if (item.unit! === "HOURS") {
                if (item.quantity && typeof(item.quantity) === 'string')
                    item.quantity = dateFormat.parseDuration(item.quantity) / 60
                if (item.invoicedQuantity && typeof(item.invoicedQuantity) === 'string')
                    item.invoicedQuantity = dateFormat.parseDuration(item.invoicedQuantity) / 60
            } else {
                if (item.quantity)
                    item.quantity = item.quantity != "" ? dateFormat.parseFloat(item.quantity) : item.quantity
                if (item.invoicedQuantity)
                    item.invoicedQuantity =
                        item.invoicedQuantity != ""
                            ? dateFormat.parseFloat(item.invoicedQuantity)
                            : item.invoicedQuantity
            }
            delete item.__typename // f this 4ever
        })
        return copy
    }

    assignedWorkReportForJob: boolean = false
    @Watch("currentWorkReportForJob", { immediate: true })
    assignWorkReportForJob() {
        if (this.currentWorkReportForJob && this.currentWorkReportForJob.uuid && !this.assignedWorkReportForJob) {
            for (var field in this.currentWorkReportForJob) {
                if (field === "__typename") continue
                Vue.set(this.workReport, field, this.currentWorkReportForJob[field])
            }
            this.workReport.jobUuid = this.jobUuid
            this.assignedWorkReportForJob = true
        }
    }

    // TODO use replacedByTemplate to mark fields
    // that have been overriden by template values
    // and maybe let user choose to keep template
    // values or restore to what it contained before
    replacedByTemplate: any = null
    handleReplacedFields(event: any) {
        // this only keeps values from the
        // previous "setting", i.e. if you switch
        // template and then switch template again
        // replacedByTemplate will contain the
        // values that were set by the previous template.
        // TODO make this system maybe restore what
        // was there before between template switching?
        this.replacedByTemplate = event
    }

    saving: boolean = false
    get loading() {
        return (
            this.$apollo.queries.workReport.loading ||
            this.$apollo.queries.currentWorkReportForJob.loading ||
            this.$apollo.queries.me.loading ||
            this.$apollo.queries.groups.loading ||
            this.saving
        )
    }

    hasMultipleActiveGroups() {
        return this.me && this.me.groupMemberships && this.me.groupMemberships!.filter((m: any) => m.active).length > 1
    }

    @Watch("me")
    watchMe() {
        navbar?.setOptions(this.options)
        if (this.me && this.new && !this.model.groupUuid && this.me.primaryGroupUuid) {
            this.model.groupUuid = this.me.primaryGroupUuid
        }
    }

    @Watch("changed")
    watchChanged() {
        this.$root.$emit("setUnsavedChanges", this.changed)
    }

    get options(): NavbarOption[] {
        if (!this.me || !this.me.permissions) return []
        var options = [
            { icon: "done", action: this.save },
            {
                title: this.$t("excludedFields"),
                action: () => {
                    this.selectingExcludedFields = true
                },
            },
            {
                title: this.$t("requiredFields"),
                action: () => {
                    this.selectingRequiredFields = true
                },
            },
        ]
        return options
    }

    changeStartEndPause() {
        var startTime: number = this.model.startTime
        var endTime: number = this.model.endTime
        var pauseDuration: number = this.model.pauseDuration
        if (startTime && endTime) {
            if (endTime < startTime) endTime += 24 * 60 * 60 * 1000;
            var duration: number = (endTime - startTime) / 60000
            if (pauseDuration && !isNaN(pauseDuration)) duration -= pauseDuration

            // round up to nearest X minutes, according to group conf or 30 by default
            var rounding: number = 30
            var roundUpwards: boolean = false
            var minimum: number = 0
            var groupConfiguration: any = this.groupConfiguration
            if (groupConfiguration) {
                rounding = parseInt(groupConfiguration.work_duration_rounding!)
                minimum = parseInt(groupConfiguration.min_work_duration!)
                roundUpwards = groupConfiguration.always_round_upwards! === "true"
            }
            if (rounding > 0) {
                var mod: number = duration % rounding
                duration += (roundUpwards && mod > 0) || mod >= rounding / 2 ? rounding - mod : -mod
            }
            if (duration < minimum) duration = minimum

            if (this.hasItems) {
                var existingWorkHourItem: any
                this.model.items.forEach((item: any) => {
                    if (item.workHours && item.unit === "HOURS") {
                        if (!existingWorkHourItem) existingWorkHourItem = item
                    }
                })
                if (existingWorkHourItem) {
                    Vue.set(existingWorkHourItem, "quantity", dateFormat.duration(duration))
                    Vue.set(this.model.items, this.model.items.indexOf(existingWorkHourItem), existingWorkHourItem)
                    this.itemsFieldUpdateKey++
                }
            } else {
                ;(this.$refs.workDuration as any).changeSilent(duration)
            }
        }
    }

    get groupConfiguration(): any {
        if (!this.groups || !this.model || !this.model.groupUuid) return false
        var group = this.groups!.find((group) => group.uuid === this.model.groupUuid)
        return group && group.configuration && JSON.parse(group.configuration)
    }

    get hasItems(): boolean {
        return this.model!.items!.length > 0
    }

    get titleExtended(): string {
        var title: string = ""
        if (this.model.title) title = this.model.title
        else if (this.model.jobTitle) title = this.model.jobTitle
        else if (this.model.assetName) {
            title = (this.model.assetNo ? this.model.assetNo + " - " : "") + this.model.assetName
            if (this.model.activityName) title = this.model.activityName + ": " + title
        } else if (this.model.customerName) {
            title = this.model.customerName
            if (this.model.activityName) title = this.model.activityName + ": " + title
            if (this.model.customerProjectNo || this.model.customerProjectName) {
                title += ":"
                if (this.model.customerProjectNo) title += " " + this.model.customerProjectNo
                if (this.model.customerProjectName) title += " " + this.model.customerProjectName
            }
        } else if (this.model.activityName) title = this.model.activityName
        return title
    }

    clearErrorRequired(field: string) {
        this.errorRequiredFields = this.errorRequiredFields.filter((e) => e !== field)
    }

    get errorRequired(): any {
        return this.errorRequiredFields
            ? this.errorRequiredFields.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
            : null
    }

    verifyRequired() {
        var input: any = this.getInputObj(this.model)
        var errorRequiredFields: string[] = []
        if (input.requiredFields) {
            if (input.requiredFields.includes("activity"))
                if (!input["activityId"]) errorRequiredFields.push("activity")
            if (input.requiredFields.includes("customer"))
                if (!input["customerUuid"]) errorRequiredFields.push("customer")
            if (input.requiredFields.includes("customerProject"))
                if (!input["customerProjectUuid"]) errorRequiredFields.push("customerProject")
            if (input.requiredFields.includes("description"))
                if (!input["description"]) errorRequiredFields.push("description")
            if (input.requiredFields.includes("group")) if (!input["groupUuid"]) errorRequiredFields.push("group")
            if (input.requiredFields.includes("startTime"))
                if (!input["startTime"]) errorRequiredFields.push("startTime")
            if (input.requiredFields.includes("endTime")) if (!input["endTime"]) errorRequiredFields.push("endTime")
            if (input.requiredFields.includes("pauseDuration"))
                if (!input["pauseDuration"] && !(this.$refs.pauseDuration as any)?.getZeroSet) errorRequiredFields.push("pauseDuration")
            if (input.requiredFields.includes("workDuration")){
                var existingWorkHourItem = this.model?.items && this.model?.items?.length > 0 && this.model.items.find((item: any) => item.workHours && item.unit === "HOURS")
                if (existingWorkHourItem && !existingWorkHourItem.quantity) errorRequiredFields.push("workDuration")
                else if (!input["workDuration"] && this.$refs.workDuration && !(this.$refs.workDuration as any)?.getZeroSet) errorRequiredFields.push("workDuration")
                this.itemsFieldUpdateKey++
            }
            if (input.requiredFields.includes("vehicle")) if (!input["vehicleId"]) errorRequiredFields.push("vehicle")
        }
        if (this) this.errorRequiredFields = errorRequiredFields
        if (input.customFields && input.customFields.length && input.customFields.length > 0) {
            if (this.$refs && this.$refs.customFields && typeof this.$refs.customFields == "object") {
                var customFields = Object(this.$refs.customFields)
                if (!customFields.canBeSaved()) return false
            }
        }
        if (errorRequiredFields.length == 0) return true
        return false
    }

    discardChanges() {
        this.changed = false
        this.discardChangesNext()
        setTimeout(() => {
            Object.assign(this.model, this.undoModel)
            this.undoModel = {}
            if (this.$apollo.queries.workReport && typeof this.$apollo.queries.workReport.refetch == "function")
                this.$apollo.queries.workReport.refetch()
            if (
                this.$apollo.queries.currentWorkReportForJob &&
                typeof this.$apollo.queries.currentWorkReportForJob.refetch == "function"
            )
                this.$apollo.queries.currentWorkReportForJob.refetch()
        }, 100)
    }
}
